import React, {useState, useMemo} from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  Stack,
  Divider,
  IconButton,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import LoadingButton from '@mui/lab/LoadingButton';
import {useFlags} from 'launchdarkly-react-client-sdk';
import Loading from '../../Loading';
import {useCreateFolderMutation} from '../../../data/documentWorkspaceApi';
import PropTypes from 'prop-types';
import {DARK_TEXT_COLOR, WHITE_100} from '../../../theme';
import {FOLDER_YEARS, FOLDER_PURPOSES} from '../../../constants';

const styles = {
  modal: {
    base: {
      borderRadius: '0.5rem',
      boxShadow: '0px 2px 8px 0px rgba(0, 0, 0, 0.15)',
      backgroundColor: WHITE_100,
      minHeight: '220px',
    },
    title: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      alignSelf: 'stretch',
      color: '#162523',
      fontSize: '14px',
      fontStyle: 'normal',
      fontWeight: '700',
      lineHeight: '150%',
      letterSpacing: '-0.14px',
    },
    divider: {
      borderBottomWidth: '0.0625rem',
      opacity: 1,
      color: '#DDe0DE',
    },
    label: {
      fontSize: '0.875rem',
      fontWeight: '500',
      lineHeight: '140%',
      color: DARK_TEXT_COLOR,
    },
    labelInfo: {
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '130%',
      color: '#819190',
      letterSpacing: '-0.12px',
    },
  },
  createButton: {
    background: '#26A993',
    borderRadius: '0.5rem',
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    minWidth: '9.5rem',
    color: '#FFFFFF',
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#229884',
    },
    '&:disabled': {
      backgroundColor: '#81C3B8',
      color: '#FFFFFF',
    },
  },
  cancelButton: {
    borderRadius: '0.5rem',
    background: WHITE_100,
    paddingLeft: '1rem',
    paddingRight: '1rem',
    height: '2.25rem',
    color: DARK_TEXT_COLOR,
    fontSize: '0.875rem',
    fontWeight: '400',
    textTransform: 'none',
    '&:hover': {
      backgroundColor: '#F5F5F5',
    },
  },
};

const CreateFolderModal = ({
  open,
  onClose,
  onSuccess,
  onError,
  expandedYears,
  setExpandedYears,
  setNewlyCreatedFolderId,
}) => {
  const [newFolder, setNewFolder] = useState({
    name: '',
    year: '',
    isExternalPreparation: false,
  });
  const [loading, setLoading] = useState(false);

  const {pdfReviewTool: pdfReviewToolEnabled} = useFlags();
  const [createFolder] = useCreateFolderMutation();
  const years = useMemo(() => FOLDER_YEARS, []);

  const handleChanges = (e) => {
    setNewFolder({...newFolder, [e.target.name]: e.target.value});
  };

  const handleSubmit = async () => {
    let result = null;
    const {name, year, isExternalPreparation} = newFolder;
    if (name && year) {
      setLoading(true);
      try {
        result = await createFolder({
          data: {
            name,
            year,
            purpose: isExternalPreparation
              ? FOLDER_PURPOSES.pdfReview
              : FOLDER_PURPOSES.regular,
          },
        });

        if (result.error) {
          throw new Error(result.error);
        }

        onClose();
        onSuccess(`Folder "${name}" created successfully`);
        if (!expandedYears.includes(year)) {
          setExpandedYears([...expandedYears, year]);
        }

        setNewlyCreatedFolderId(result.data?.record?.id || result.data?.id);
        setNewFolder({name: '', year: '', isExternalPreparation: false});
        setTimeout(() => {
          setNewlyCreatedFolderId(null);
        }, 3000);
      } catch (error) {
        console.error('Error creating folder:', error);
        if (onError) {
          onError(`Failed: ${result?.error?.data?.error}`);
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        position={'absolute'}
        sx={{left: '50%', top: '50%', transform: 'translate(-50%, -50%)'}}
      >
        <Box width={'500px'} minWidth={'500px'} maxWidth={'500px'}>
          <Stack direction={'column'} sx={styles.modal.base}>
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Typography sx={{...styles.modal.title, fontSize: '16px'}}>
                Create new folder
              </Typography>
              <IconButton size={'small'} onClick={onClose}>
                <CloseIcon size={'1.25rem'} />
              </IconButton>
            </Stack>

            <Divider orientation={'horizontal'} sx={styles.modal.divider} />

            <Stack direction={'column'} padding={'1.5rem'} spacing={'1.5rem'}>
              <Stack direction="column" spacing="0.5rem">
                <Typography sx={styles.modal.label}>Year *</Typography>
                <RadioGroup
                  row
                  name="year"
                  value={newFolder.year}
                  onChange={(e) => handleChanges(e)}
                  sx={{gap: '0.5rem'}}
                >
                  {years.map((year) => (
                    <FormControlLabel
                      key={year}
                      value={year}
                      control={
                        <Radio
                          size="small"
                          icon={<RadioButtonUncheckedIcon />}
                          checkedIcon={<RadioButtonCheckedIcon />}
                          sx={{
                            color: '#DDE0DE',
                            '&.Mui-checked': {
                              color: '#26A993',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '1.25rem',
                            },
                          }}
                        />
                      }
                      label={year}
                      sx={{
                        margin: 0,
                        padding: '0.1rem 0.5rem 0.1rem 0.1rem',
                        border:
                          newFolder.year === year
                            ? '1.5px solid #26A993'
                            : '1px solid #DDE0DE',
                        borderRadius: '0.5rem',
                        color: DARK_TEXT_COLOR,
                        '&:hover': {
                          borderColor: '#26A993',
                        },
                      }}
                    />
                  ))}
                </RadioGroup>
              </Stack>
              <Stack direction="column" spacing="0.5rem">
                <Typography sx={styles.modal.label}>Folder name *</Typography>
                <TextField
                  autoFocus
                  fullWidth
                  name="name"
                  value={newFolder.name}
                  onChange={(e) => handleChanges(e)}
                  placeholder="Enter folder name"
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '0.5rem',
                      height: '2.5rem',
                      '&:hover fieldset': {
                        borderColor: '#26A993',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#26A993',
                      },
                    },
                  }}
                />
              </Stack>
              {pdfReviewToolEnabled && (
                <Stack direction="row" spacing="0.5rem">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={newFolder.isExternalPreparation}
                        onChange={(e) =>
                          setNewFolder({
                            ...newFolder,
                            isExternalPreparation: e.target.checked,
                          })
                        }
                        sx={{
                          width: 32,
                          height: 20,
                          padding: 0,
                          '& .MuiSwitch-switchBase': {
                            padding: '2px',
                            '&.Mui-checked': {
                              transform: 'translateX(12px)',
                              color: '#26A993',
                              '& + .MuiSwitch-track': {
                                backgroundColor: '#26A993',
                                opacity: 1,
                              },
                            },
                          },
                          '& .MuiSwitch-thumb': {
                            width: 16,
                            height: 16,
                            backgroundColor: '#687b7a',
                          },
                          '& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb':
                            {
                              backgroundColor: '#fff',
                            },
                          '& .MuiSwitch-track': {
                            borderRadius: 100,
                            backgroundColor: '#e1e5e4',
                            opacity: 1,
                          },
                        }}
                      />
                    }
                    labelPlacement="start"
                    sx={{width: '100%', justifyContent: 'space-between'}}
                    label={
                      <>
                        <Typography sx={styles.modal.label}>
                          Externally prepared financials
                        </Typography>
                        <Typography sx={styles.modal.labelInfo}>
                          Creates a read-only folder for externally prepared
                          PDFs and enables AI PDF review.
                        </Typography>
                      </>
                    }
                  />
                </Stack>
              )}
            </Stack>
            <Divider orientation={'horizontal'} sx={styles.modal.divider} />
            <Stack
              direction={'row'}
              paddingY={'1rem'}
              paddingLeft={'1.5rem'}
              paddingRight={'1rem'}
              justifyContent={'flex-end'}
              alignItems={'center'}
              spacing={'0.625rem'}
            >
              <Button sx={styles.cancelButton} onClick={onClose}>
                Cancel
              </Button>
              <LoadingButton
                variant={'contained'}
                sx={{
                  ...styles.createButton,
                  '&.MuiLoadingButton-loading': {
                    backgroundColor: '#26A993',
                  },
                }}
                onClick={handleSubmit}
                disabled={!newFolder.name || !newFolder.year}
                loading={loading}
                loadingIndicator={
                  <Loading
                    loading={loading}
                    loadingProps={{color: WHITE_100}}
                  />
                }
              >
                {!loading && 'Create new folder'}
              </LoadingButton>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Modal>
  );
};

export default CreateFolderModal;

CreateFolderModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onError: PropTypes.func,
  expandedYears: PropTypes.array,
  setExpandedYears: PropTypes.func,
  setNewlyCreatedFolderId: PropTypes.func,
};
